import { Button, formatDateTime, PersonDisplay } from '@dataroom/shared';
import type { AdminSigningEnvelope, RoomDocument, SigningEnvelopeStatus } from '@dataroom/shared';
import { Modal } from './Modal';

interface AgreementDetailModalProps {
  envelope: AdminSigningEnvelope;
  /** The room's documents, to resolve the envelope's document name if known. */
  documents: RoomDocument[];
  onClose: () => void;
  onCancel: () => void;
  cancelPending: boolean;
  cancelError: string | null;
}

const STEPS: Array<{ key: SigningEnvelopeStatus; label: string }> = [
  { key: 'created', label: 'Issued' },
  { key: 'consent_recorded', label: 'Consent' },
  { key: 'signed', label: 'Signed' },
  { key: 'sealed', label: 'Sealed' },
];

/**
 * PRD-0004 §3c "Agreements" row detail — clicking an issued envelope in
 * {@link AgreementsCard} opens this to show the signing progress (created ->
 * consent_recorded -> signed -> sealed) and, for an unsealed/uncancelled
 * envelope, a Cancel (void) action. A cancelled envelope can never seal or
 * advance the buyer's funnel (enforced server-side); this view just reflects
 * that state — it never lets the admin cancel a sealed envelope.
 */
export function AgreementDetailModal({
  envelope,
  documents,
  onClose,
  onCancel,
  cancelPending,
  cancelError,
}: AgreementDetailModalProps) {
  const cancelled = envelope.cancelledAt !== null;
  const currentIndex = STEPS.findIndex((s) => s.key === envelope.status);
  const document = documents.find((d) => d.id === envelope.documentId) ?? null;
  const canCancel = !cancelled && envelope.status !== 'sealed';

  return (
    <Modal
      title="Agreement detail"
      description={envelope.signerEmail ?? envelope.signerActorId}
      onClose={onClose}
      footer={
        <>
          {cancelError ? (
            <p className="text-sm text-destructive">{cancelError}</p>
          ) : (
            <span className="text-xs text-muted-foreground">
              {cancelled
                ? 'This agreement was cancelled — it can no longer be signed.'
                : envelope.status === 'sealed'
                  ? 'Sealed agreements cannot be cancelled.'
                  : 'Cancelling voids this agreement; it can never be signed after that.'}
            </span>
          )}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={cancelPending}>
              Close
            </Button>
            {canCancel ? (
              <Button
                variant="destructive"
                size="sm"
                onClick={onCancel}
                disabled={cancelPending}
              >
                {cancelPending ? 'Cancelling…' : 'Cancel agreement'}
              </Button>
            ) : null}
          </div>
        </>
      }
    >
      <div className="space-y-4 text-sm">
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
          <dt className="text-muted-foreground">Signer</dt>
          <dd>
            <PersonDisplay email={envelope.signerEmail} fallback={envelope.signerActorId} size="sm" />
          </dd>
          <dt className="text-muted-foreground">Document</dt>
          <dd>{document ? document.name : envelope.documentId}</dd>
          <dt className="text-muted-foreground">Issued</dt>
          <dd>{formatDateTime(envelope.createdAt)}</dd>
          {cancelled ? (
            <>
              <dt className="text-muted-foreground">Cancelled</dt>
              <dd>{formatDateTime(envelope.cancelledAt)}</dd>
            </>
          ) : null}
        </dl>

        <div>
          <p className="mb-2 text-sm font-medium">Signing progress</p>
          {cancelled ? (
            <span className="inline-flex rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive">
              Cancelled
            </span>
          ) : (
            <ol className="flex items-center gap-2">
              {STEPS.map((step, i) => {
                const done = i < currentIndex;
                const current = i === currentIndex;
                return (
                  <li key={step.key} className="flex items-center gap-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        current
                          ? 'bg-st-certified text-st-certified-fg'
                          : done
                            ? 'bg-muted text-foreground'
                            : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {step.label}
                    </span>
                    {i < STEPS.length - 1 ? (
                      <span className="text-muted-foreground">→</span>
                    ) : null}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </Modal>
  );
}
